import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import * as _ from "lodash";
import { Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BuildingSurveyViewModel } from "src/app/shared/Models/buildingTableViewModel";
import { MainDistributionEnum } from "src/app/shared/Models/enums/MainDistributionEnum";
import { MainDistributionUndergroundReason } from "src/app/shared/Models/enums/MainDistributionUndergroundReason";
import { OtherObstructionType } from "src/app/shared/Models/enums/OtherObstructionType";
import { StreetFurnitureType } from "src/app/shared/Models/enums/StreetFurnitureType";
import {
  AuditLogRecord,
  AuditLogRecordUpdate,
} from "src/app/shared/Models/read";
import { AuditLogService } from "src/app/shared/services/audit-log.service";
import { LoaderService } from "src/app/shared/services/loader.service";

const addBuildingRangeToBuildingGroupCommand = $localize`:@@auditlog.AddBuildingRangeToBuildingGroupCommand:Building added to group`;

enum CommandsDictionary {
  "AddBuildingRangeToBuildingGroupCommand" = "Building added to group",
  "AssignAddressToExistingBuildingCommand" = "New address assigned",

  "ChangeUnitInfoCommand" = "Unit info changed",
  "CreateBuildingCommand" = "Building created",
  "CreateBuildingWithExistingAddressCommand" = "Building created",
  "ChangeOutsideEngineerCommand" = "Outside engineer changed",
  "CreateInsideSurveyCommand" = "Inside survey created",
  "CreateOutsideSurveyCommand" = "Outside survey created",
  "ChangeOutsideSurveyPhotoRemarksCommand" = "Outside photo remarks",
  "CompleteSurveyCommand" = "Survey completed",
  "RejectInsideSurveyCommand" = "Inside survey rejected",
  "RejectSurveyCommand" = "Outside survey rejected",
  "UpdateInsideSurveyBuildingLayoutCommand" = "Building layout updated",
  "UpdateInsideSurveyTechnicalInfoCommand" = "Inside technical info updated",
  "UpdateSurveyCommand" = "Outside survey updated",
  "ValidateInsideSurveyCommand" = "Inside survey updated",
  "ValidateSurveyCommand" = "Outside survey validated",

  "AddExistingPersonToBuildingCommand" = "Person added",
  "AddNewPersonToBuildingCommand" = "Person added",
  "RemovePersonFromBuildingCommand" = "Person removed",
}

export interface RowViewModel {
  label: string;
  value: string;
}

interface LogShortViewModel {
  id: string;
  date: string;
  type: string;
  selected: Observable<boolean>;
  select: () => void;
}

interface LogDetailsViewModel {
  id: string;
  date: string;
  type: string;

  rows: RowViewModel[];
}

@Component({
  selector: "app-audit-log-modal",
  templateUrl: "./audit-log-modal.component.html",
  styleUrls: ["./audit-log-modal.component.scss"],
})
export class AuditLogModalComponent implements OnInit, OnDestroy {
  items: AuditLogRecord[];

  records: LogShortViewModel[];
  selectedRecord: LogDetailsViewModel;

  loading: boolean;

  @Input()
  building: BuildingSurveyViewModel;

  constructor(
    private readonly service: AuditLogService,
    private readonly loaderService: LoaderService,
    public readonly activeModal: NgbActiveModal
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.loaderState
      .pipe(
        takeUntil(this.destroy$),
        map((x) => x.show)
      )
      .subscribe((x) => (this.loading = x));

    this.items = await this.service.getAuditLogRecords(
      this.building.buildingId
    );
    this.records = this.createShortList(this.items);
  }

  createShortList(models: AuditLogRecord[]): LogShortViewModel[] {
    const selectedObservables = _(models)
      .keyBy((x) => x.id)
      .mapValues((x) => new Subject<boolean>())
      .value();

    return models.map((x) => {
      const currentObservable = selectedObservables[x.id];
      return {
        ...x,
        date: this.formatDate(x.date),
        type: this.formatType(x.type),
        selected: currentObservable,
        select: () => {
          _(selectedObservables)
            .filter((x1, id1) => id1 !== x.id)
            .forEach((x1) => x1.next(false));
          currentObservable.next(true);
          const model = this.items.find((x1) => x1.id === x.id);
          this.selectedRecord = this.createDetails(model);
        },
      };
    });
  }

  createDetails(model: AuditLogRecord): LogDetailsViewModel {
    return {
      ...model,
      date: this.formatDate(model.date),
      type: this.formatType(model.type),
      rows: this.createRows(model),
    };
  }

  createRows(model: AuditLogRecord): RowViewModel[] {
    let rows: RowViewModel[] = [];

    if (model.date)
      rows.push({
        label: "Date",
        value: this.formatDate(model.date),
      });

    const label = "Done By";
    if (model.executedByUser)
      rows.push({
        label,
        value: `${model.executedByUser.firstName} ${model.executedByUser.lastName}`,
      });
    else
      rows.push({
        label,
        value: model.executedBy || "unknown",
      });

    if (model.address)
      rows.push({
        label: "Address",
        value: `${model.address.street} ${model.address.number} ${model.address.postalCode} ${model.address.city}`,
      });

    if (model.lomKey)
      rows.push({
        label: "Building Key",
        value: this.formatString(model.lomKey),
      });

    if (model.lamKey)
      rows.push({
        label: "Address key",
        value: this.formatString(model.lamKey),
      });

    if (model.engineer)
      rows.push({
        label: "Engineer",
        value: `${model.engineer.firstName} ${model.engineer.lastName}`,
      });

    if (model.buildingGroup)
      rows.push({
        label: "Building group",
        value: `${model.buildingGroup.name}`,
      });

    if (model.person)
      rows.push({
        label: "Person",
        value: `${model.person.name}`,
      });

    if (model.units) {
      rows.push({
        label: "Floors",
        value: model.units.numberOfFloors.toString(),
      });
      rows.push({
        label: "SU",
        value: model.units.numberOfServiceUnits.toString(),
      });
      rows.push({
        label: "BU",
        value: model.units.numberOfBusinessUnits.toString(),
      });
      rows.push({
        label: "LU",
        value: model.units.numberOfLivingUnits.toString(),
      });
    }

    if (model.outsideSurvey)
      rows = rows.concat(this.createUpdateRows(model.outsideSurvey));

    if (model.coordinates)
      model.coordinates
        .sort((x) => x.type)
        .forEach((x) => {
          let typeLabel = "";
          if (x.type === 0) typeLabel = " (Lambert 72)";
          else if (x.type === 1) typeLabel = " (WGS 84)";

          rows.push({
            label: "XY Coordinates" + typeLabel,
            value: `${this.formatCoord(x.posX)}/${this.formatCoord(x.posY)}`,
          });
        });

    if (model.facade) {
      rows.push({
        label: "Facade photo remark",
        value: model.validatorRemark,
      });
      model.facade.facadeDetailPhotoRemarks.forEach((x, i) => {
        rows.push({
          label: `Facade detail photo remark ${i}`,
          value: this.formatString(x),
        });
      });
    }

    if (model.validatorRemark)
      rows.push({
        label: "Remarks",
        value: model.validatorRemark,
      });

    return rows;
  }

  createUpdateRows(update: AuditLogRecordUpdate): RowViewModel[] {
    const rows: RowViewModel[] = [];
    rows.push({
      label: "Coax cables",
      value: this.formatBool(update.coaxCables),
    });
    rows.push({
      label: "Electricity cables",
      value: this.formatBool(update.electricityCables),
    });
    rows.push({
      label: "Facade remark",
      value: this.formatString(update.facadeRemark),
    });

    if (update.flagGeneralBuildingRemark)
      rows.push({
        label: "General remark",
        value: this.formatString(update.generalBuildingRemark),
      });

    rows.push({
      label: "Front yard",
      value: this.formatBool(update.frontYard),
    });
    rows.push({
      label: "Main distribution",
      value: this.formatMainDistributionEnum(update.mainDistribution),
    });
    rows.push({
      label: "Main distribution front yard length",
      value: this.formatNumber(update.mainDistributionFrontYardLength),
    });
    rows.push({
      label: "Main distribution front yard pavement",
      value: this.formatNumber(update.mainDistributionFrontYardLength),
    });
    rows.push({
      label: "Main distribution height",
      value: this.formatNumber(update.mainDistributionHeight),
    });
    rows.push({
      label: "Main distribution underground reason",
      value: this.formatMainDistributionUndergroundReason(
        update.mainDistributionUndergroundReason
      ),
    });
    rows.push({
      label: "Multi tap",
      value: this.formatBool(update.multiTap),
    });

    rows.push({
      label: "Other obstruction",
      value: this.formatBool(update.otherObstruction),
    });
    if (update.otherObstruction)
      rows.push({
        label: "Other obstruction",
        value: this.formatString(
          OtherObstructionType[update.otherObstructionType]
        ),
      });

    rows.push({
      label: "Patrimony",
      value: this.formatBool(update.patrimony),
    });

    rows.push({
      label: "Paving footpath",
      value: this.formatBool(update.pavingFootpath),
    });
    rows.push({
      label: "Public lighting",
      value: this.formatBool(update.publicLightning),
    });

    rows.push({
      label: "Street Furniture",
      value: this.formatBool(update.streetFurniture),
    });
    if (update.streetFurniture)
      rows.push({
        label: "Street furniture type",
        value: this.formatString(
          StreetFurnitureType[update.streetFurnitureType]
        ),
      });

    rows.push({
      label: "Street Remark",
      value: this.formatString(update.streetRemark),
    });
    return rows;
  }

  formatString(str: string): string {
    return str || "-";
  }

  formatDate(date: Date): string {
    const dtf = new Intl.DateTimeFormat("en", {
      year: "numeric",
      month: "numeric",
      day: "2-digit",
    });
    const [{ value: mo }, , { value: da }, , { value: ye }] =
      dtf.formatToParts(date);
    return `${da}/${mo}/${ye}`;
  }

  formatType(type: string): string {
    return CommandsDictionary[type] || type;
  }

  formatBool(bool: boolean): string {
    return bool ? "Yes" : "No";
  }

  formatNumber(num: number): string {
    if (num === undefined || num === null) return "-";

    return num.toString();
  }

  formatCoord(num: number) {
    if (num === undefined || num === null) return "-";

    return num.toFixed(4);
  }

  formatMainDistributionEnum(val: MainDistributionEnum) {
    let res = "";
    switch (val) {
      case MainDistributionEnum.Facade:
        res = "Facade";
        break;
      case MainDistributionEnum.Underground:
        res = "Underground";
        break;
    }
    return this.formatString(res);
  }

  formatMainDistributionUndergroundReason(
    val: MainDistributionUndergroundReason
  ) {
    let res = "";
    switch (val) {
      case MainDistributionUndergroundReason.Gable:
        res = "Gable";
        break;
      case MainDistributionUndergroundReason.Glass:
        res = "Glass";
        break;
      case MainDistributionUndergroundReason.Height:
        res = "Height";
        break;
      case MainDistributionUndergroundReason.Irregular:
        res = "Irregular";
        break;
      case MainDistributionUndergroundReason.Monument:
        res = "Monument";
        break;
      case MainDistributionUndergroundReason.NotApplicable:
        res = "Not Applicable";
        break;
      case MainDistributionUndergroundReason.Ornamental:
        res = "Ornamental";
        break;
    }
    return this.formatString(res);
  }
}
